// src/components/accueil/AccueilOperateur.tsx — SCE Issues
// Accueil V2 de l'opérateur (Lot 9, maquette validée le 25/09) : déclarer,
// suivre ses déclarations, vérifier que le problème n'est pas déjà déclaré.
//
//   Bureau (≥ 1100 px) : trois colonnes à hauteur d'écran —
//     déclarer + du nouveau sur mes déclarations + mes déclarations ·
//     derniers problèmes déclarés · activité récente.
//   Téléphone : déclarer, du nouveau, derniers problèmes ; cinq lignes au plus.
//
// Aussi l'accueil d'un fournisseur : sa liste ne contient que ses
// déclarations (useIssues), l'activité est donc cloisonnée d'elle-même.
//
// Lot 12 : les problèmes suivis s'ajoutent à « Du nouveau sur vos
// déclarations », et ont leur carte dès qu'on en suit un. Lot 12 ter : carte
// « Éléments suivis » (CarteElementsSuivis), dès qu'on suit quoi que ce soit.
//
// Mon accueil, mes files (lot 8 du chantier du 03/11, docs/chantier-accueil.md) :
// les colonnes viennent de la disposition (modèle « operateur » + différences),
// comme chez le coordinateur, sans compteurs. Le modèle du code est l'accueil
// d'avant ; un fournisseur le garde toujours.

import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { useChoixDeclaration } from '@/components/layout/ChoixDeclaration'
import { LayoutDashboard, Plus } from 'lucide-react'
import type { Issue } from '@/types/issue.types'
import {
  derniersDeclares,
  evenementsAccueil,
  libelleAge,
  nouveautesDeclarant,
  regrouperFil,
  nouveautesParProbleme,
  salutation,
} from '@/utils/accueil'
import type { DonneesAccueil } from '@/hooks/useAccueil'
import type { ListeProblemes } from '@/components/dashboard/ListeProblemesSheet'
import { SuiteListe } from '@/components/layout/SuiteProblemes'
import { problemesSuivis } from '@/utils/abonnements'
import type { ElementsSuivis } from '@/utils/elementsSuivis'
import { CarteAccueil, PiedCarte, SqueletteLignes, VideCarte } from './CarteAccueil'
import { aDesSuivis, CarteElementsSuivis } from './CarteElementsSuivis'
import { FilActivite, LigneFilActivite } from './FilActivite'
import { LigneProbleme } from './LigneProbleme'
import { RechercheGlobale } from './RechercheGlobale'
import { CarteActionsFnct } from './CarteActionsFnct'
import { CarteRevueFnct } from './CarteRevueFnct'
import { EtatProbleme } from '@/components/ui/EtatProbleme'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useDispositionAccueil } from '@/hooks/useDispositionAccueil'
import { CARTES_ACCUEIL, cartesAuTelephone, disposer, estIdCarte, type Rangement } from '@/utils/dispositionAccueil'
import { carteDisponible } from './disponibiliteCartes'
import { ColonnesEditables } from './personnaliser/ColonnesEditables'
import { BarrePersonnaliser } from './personnaliser/BarrePersonnaliser'
import { ReglageTelephone } from './personnaliser/ReglageTelephone'

const LIGNES_TELEPHONE = 5
const HAUTEUR_COLONNES =
  'max(460px, calc(100dvh - var(--spacing-header-h) - 68px - var(--hauteur-bandeau-vue, 0px) - 56px))'

const STYLE_GRILLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1.15fr 1.25fr 0.9fr',
  gridTemplateRows: 'minmax(0, 1fr)',
  gap: 20,
  height: HAUTEUR_COLONNES,
}

// Place de chaque carte dans sa colonne, comme avant le chantier : Déclarer à sa
// hauteur, les petites plafonnées, Mes déclarations prend le reste.
const PLAFONDS: Record<string, string> = { revueFnct: '30%', actionsFnct: '30%', nouveautes: '40%', suivis: '30%' }

function placeCarte(id: string, seule: boolean): CSSProperties {
  const grande = estIdCarte(id) && CARTES_ACCUEIL[id].grande
  if (grande || seule) return { flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column' }
  if (id === 'declarer') return { flexShrink: 0, display: 'flex', flexDirection: 'column' }
  const plafond = PLAFONDS[id]
  return { flex: '0 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', ...(plafond ? { maxHeight: plafond, flexShrink: 0 } : {}) }
}

export function AccueilOperateur({
  issues,
  loading,
  accueil,
  prenom,
  moi,
  mesDeclarations,
  monId,
  upn,
  elementsSuivis,
  maintenant,
  large,
  ouvrirListe,
}: {
  issues: Issue[]
  loading: boolean
  accueil: DonneesAccueil
  prenom: string
  moi: string
  mesDeclarations: Issue[]
  /** Identifiant SharePoint de la personne affichée (abonnements, Lot 12). */
  monId: number | null
  upn: string
  /** PN et N° de série suivis (Lots 12 bis et ter) : leurs problèmes actifs s'ajoutent aux suivis. */
  elementsSuivis: ElementsSuivis
  maintenant: number
  large: boolean
  ouvrirListe: (liste: ListeProblemes) => void
}): JSX.Element {
  const navigate = useNavigate()
  const choix = useChoixDeclaration()
  const { isFournisseur } = useCurrentUser()

  // La disposition (lot 8) : modèle « operateur » + différences de la personne.
  const disponible = useCallback((id: string) => carteDisponible(id, upn, isFournisseur, 'operateur'), [upn, isFournisseur])
  const dispo = useDispositionAccueil('operateur', disponible)
  const [brouillon, setBrouillon] = useState<Rangement | null>(null)
  const [reglageTel, setReglageTel] = useState(false)
  const colonnes = brouillon?.colonnes ?? dispo.disposition.colonnes
  const modeleSeul = useMemo(() => disposer(dispo.modele, null, disponible), [dispo.modele, disponible])
  const auModele = !!brouillon && JSON.stringify(brouillon.colonnes) === JSON.stringify(modeleSeul.colonnes)
  useEffect(() => {
    if (!large) setBrouillon(null)
  }, [large])

  const evenements = useMemo(
    () => evenementsAccueil(accueil.evenements, issues, [], accueil.visitePrecedente),
    [accueil.evenements, issues, accueil.visitePrecedente],
  )
  const suivis = useMemo(
    () => problemesSuivis(issues, monId, upn, undefined, elementsSuivis),
    [issues, monId, upn, elementsSuivis],
  )
  // Mes déclarations ET mes suivis : « du nouveau » sur les deux.
  const mesIds = useMemo(
    () => new Set([...mesDeclarations, ...suivis].map((i) => i.id)),
    [mesDeclarations, suivis],
  )
  const titreNouveautes = suivis.length > 0 ? 'Du nouveau sur vos déclarations et suivis' : 'Du nouveau sur vos déclarations'
  const nouveautes = useMemo(
    () => (accueil.visiteLue
      ? nouveautesDeclarant(evenements, mesIds, moi, accueil.visitePrecedente, maintenant)
      : []),
    [evenements, mesIds, moi, accueil.visiteLue, accueil.visitePrecedente, maintenant],
  )
  const derniers = useMemo(() => derniersDeclares(issues, large ? 30 : LIGNES_TELEPHONE), [issues, large])

  const boutonPersonnaliser = dispo.modifiable && !brouillon && (
    <button
      type="button"
      className="perso-menu"
      aria-label={large ? 'Personnaliser l’accueil' : 'Mon accueil au téléphone'}
      title={large ? 'Personnaliser l’accueil' : 'Mon accueil au téléphone'}
      onClick={() => (large ? setBrouillon({ ...dispo.disposition }) : setReglageTel(true))}
    >
      <LayoutDashboard size={20} aria-hidden="true" />
    </button>
  )

  // « Déclarer » (06/10) : un problème ou une NC-S (layout/ChoixDeclaration).
  const declarer = (
    <>
    <button
      type="button"
      onClick={choix.basculer}
      className="flex items-center justify-center w-full"
      style={{
        gap: 10,
        height: 56,
        borderRadius: 10,
        backgroundColor: 'var(--color-yellow)',
        border: '1px solid var(--color-black)',
        fontFamily: 'inherit',
        fontSize: 18,
        fontWeight: 500,
        color: 'var(--color-black)',
        cursor: 'pointer',
      }}
    >
      <Plus size={20} strokeWidth={2.5} aria-hidden="true" />
      Déclarer
    </button>
    {choix.feuille}
    </>
  )

  const entete = (
    <div className="flex items-start justify-between" style={{ gap: 8 }}>
      <div>
        <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>{salutation(prenom, maintenant, accueil.visitePrecedente, accueil.retour?.retour ?? null)}</div>
        <h1 style={{ margin: '4px 0 0', fontSize: 25, fontWeight: 400, lineHeight: '30px' }}>Un problème sur un équipement ?</h1>
      </div>
      {boutonPersonnaliser}
    </div>
  )

  const blocNouveautes = !accueil.visiteLue || (accueil.loading && accueil.evenements.length === 0)
    ? <SqueletteLignes n={2} />
    : nouveautes.length === 0
      ? <VideCarte>Rien de nouveau sur vos déclarations{suivis.length > 0 ? ' et suivis' : ''}{accueil.visitePrecedente ? ' depuis votre dernière visite' : ' cette semaine'}.</VideCarte>
      // Un défaut sur plusieurs machines : une ligne (19/10).
      : regrouperFil(nouveautes).slice(0, large ? undefined : 3).map((l) => (
        <LigneFilActivite key={l.principal.evenement.id} ligne={l} moi={moi} maintenant={maintenant} />
      ))

  const lignesDerniers = loading && issues.length === 0
    ? <SqueletteLignes />
    : derniers.length === 0
      ? <VideCarte>Aucun problème déclaré pour le moment.</VideCarte>
      : (
        <SuiteListe libelle="Derniers problèmes déclarés" issues={derniers}>
          {derniers.map((i) => (
            <LigneProbleme
              key={i.id}
              issue={i}
              deuxLignes={!large}
              meta={[large ? i.id : '', i.numeroSerie, libelleAge(i.dateDeclaration, maintenant)].filter(Boolean).join(' · ')}
              droite={<EtatProbleme statut={i.statut} criticite={i.criticite} />}
            />
          ))}
        </SuiteListe>
      )

  const nouveautesSuivis = useMemo(
    () => (accueil.visiteLue
      ? nouveautesParProbleme(evenements, new Set(suivis.map((i) => i.id)), moi)
      : new Map()),
    [evenements, suivis, moi, accueil.visiteLue],
  )
  const suiviVisible = aDesSuivis(suivis, elementsSuivis)
  const carteSuivis = (
    <CarteElementsSuivis
      issues={issues}
      suivis={suivis}
      elements={elementsSuivis}
      nouveautes={nouveautesSuivis}
      monId={monId}
      upn={upn}
      maintenant={maintenant}
      loading={loading}
      large={large}
      ajuste={large}
      ouvrirListe={ouvrirListe}
    />
  )

  const filActivite = accueil.loading && accueil.evenements.length === 0
    ? <SqueletteLignes />
    : accueil.error && accueil.evenements.length === 0
      ? <VideCarte erreur>{accueil.error}</VideCarte>
      : evenements.length === 0
        ? <VideCarte>Aucune activité récente.</VideCarte>
        : (
          <FilActivite
            evenements={evenements}
            limite={large ? undefined : 6}
            visitePrecedente={accueil.visiteLue ? accueil.visitePrecedente : null}
            moi={moi}
            maintenant={maintenant}
          />
        )

  const listeMesDeclarations = mesDeclarations.length === 0
    ? <VideCarte>Vous n'avez rien déclaré pour le moment.</VideCarte>
    : (
      <SuiteListe libelle="Mes déclarations" issues={mesDeclarations}>
        {(large ? mesDeclarations : mesDeclarations.slice(0, LIGNES_TELEPHONE)).map((i) => (
          <LigneProbleme
            key={i.id}
            issue={i}
            deuxLignes={!large}
            meta={`${i.id} · ${libelleAge(i.dateDeclaration, maintenant)}`}
            droite={<EtatProbleme statut={i.statut} criticite={i.criticite} />}
          />
        ))}
      </SuiteListe>
    )

  // ─── Téléphone ───
  // L'ordre du modèle (celui d'avant) ou le sien ; « Mes déclarations » et
  // l'activité n'y sont que si la personne les y a fait venir (lot 8).

  if (!large) {
    const carteTelephone = (id: string): JSX.Element | null => {
      switch (id) {
        case 'declarer':
          return (
            <section key={id} className="flex flex-col" style={{ gap: 10 }}>
              {entete}
              {declarer}
            </section>
          )
        case 'revueFnct':
          return <CarteRevueFnct key={id} />
        case 'actionsFnct':
          return <CarteActionsFnct key={id} />
        case 'nouveautes':
          return (
            <CarteAccueil key={id} titre={titreNouveautes} pied={<PiedCarte libelle={`Mes déclarations (${mesDeclarations.length})`} onClick={() => navigate('/my-issues')} />}>
              {blocNouveautes}
            </CarteAccueil>
          )
        case 'suivis':
          return suiviVisible ? <div key={id}>{carteSuivis}</div> : null
        case 'derniers':
          return (
            <CarteAccueil key={id} titre="Derniers problèmes" sousTitre="Le vôtre y est peut-être déjà." pied={<PiedCarte libelle="Tous les problèmes" onClick={() => navigate('/issues')} />}>
              {lignesDerniers}
            </CarteAccueil>
          )
        case 'mesDeclarations':
          return (
            <CarteAccueil key={id} titre="Mes déclarations" compteur={mesDeclarations.length} pied={<PiedCarte libelle="Toutes mes déclarations" onClick={() => navigate('/my-issues')} />}>
              {listeMesDeclarations}
            </CarteAccueil>
          )
        case 'activite':
          return (
            <CarteAccueil key={id} titre="Activité récente" sousTitre="Tous les métiers">
              {filActivite}
            </CarteAccueil>
          )
        default:
          return null
      }
    }
    return (
      <div className="flex flex-col" style={{ gap: 18, padding: '14px 16px 24px' }}>
        <RechercheGlobale issues={issues} variante="page" />
        {cartesAuTelephone(dispo.disposition.colonnes, dispo.modele, dispo.disposition.telephone).map(carteTelephone)}
        {reglageTel && (
          <ReglageTelephone
            colonnes={dispo.disposition.colonnes}
            modele={dispo.modele}
            reglage={dispo.disposition.telephone}
            titreDe={(id) => (estIdCarte(id) ? CARTES_ACCUEIL[id].titre : id)}
            onFermer={() => setReglageTel(false)}
            onTerminer={(telephone) => {
              dispo.enregistrer({ ...dispo.disposition, telephone })
              setReglageTel(false)
            }}
          />
        )}
      </div>
    )
  }

  // ─── Bureau ───

  function carteBureau(id: string): JSX.Element | null {
    switch (id) {
      case 'declarer':
        return (
          <section
            className="flex flex-col"
            style={{ gap: 14, padding: 22, backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 12 }}
          >
            {entete}
            {declarer}
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              Moins d'une minute : l'équipement, une photo, quelques mots.
            </div>
          </section>
        )
      // Le plafond de hauteur est posé par placeCarte ; la carte s'y serre et défile.
      case 'revueFnct':
        return <CarteRevueFnct hauteurMax="100%" />
      case 'actionsFnct':
        return <CarteActionsFnct hauteurMax="100%" />
      case 'nouveautes':
        return <CarteAccueil ajuste titre={titreNouveautes}>{blocNouveautes}</CarteAccueil>
      case 'suivis':
        return suiviVisible ? carteSuivis : null
      case 'mesDeclarations':
        return (
          <CarteAccueil
            defile
            titre="Mes déclarations"
            compteur={mesDeclarations.length}
            action={
              <button type="button" onClick={() => navigate('/my-issues')} style={{ fontFamily: 'inherit', fontSize: 13, border: 'none', background: 'transparent', textDecoration: 'underline', cursor: 'pointer', color: 'var(--color-text-primary)' }}>
                Toutes
              </button>
            }
          >
            {listeMesDeclarations}
          </CarteAccueil>
        )
      case 'derniers':
        return (
          <CarteAccueil
            defile
            titre="Derniers problèmes déclarés"
            sousTitre="Avant de déclarer : le vôtre y est peut-être déjà."
            pied={<PiedCarte libelle="Tous les problèmes" onClick={() => navigate('/issues')} />}
          >
            {lignesDerniers}
          </CarteAccueil>
        )
      case 'activite':
        return (
          <CarteAccueil defile titre="Activité récente" sousTitre="Tous les métiers">
            {filActivite}
          </CarteAccueil>
        )
      default:
        return null
    }
  }

  return (
    <div className="flex flex-col" style={{ gap: 16, padding: '24px 40px' }}>
      {brouillon && (
        <BarrePersonnaliser
          auModele={auModele}
          onModele={() => setBrouillon({ ...brouillon, colonnes: modeleSeul.colonnes })}
          onAnnuler={() => setBrouillon(null)}
          onTerminer={() => {
            dispo.enregistrer(brouillon)
            setBrouillon(null)
          }}
        />
      )}
      {brouillon ? (
        <ColonnesEditables
          colonnes={brouillon.colonnes}
          modele={dispo.modele}
          disponible={disponible}
          files={[]}
          titre={(id) => id}
          onChange={(c) => setBrouillon({ ...brouillon, colonnes: c })}
          rendre={carteBureau}
          place={placeCarte}
          style={STYLE_GRILLE}
        />
      ) : (
        <div style={STYLE_GRILLE}>
          {colonnes.map((colonne, j) => (
            <div key={j} className="flex flex-col" style={{ minHeight: 0, gap: 20 }}>
              {colonne.map((id) => (
                <div key={id} className="place-carte" style={{ ...placeCarte(id, colonne.length === 1), position: 'relative' }}>
                  {carteBureau(id)}
                  {dispo.nouvelles.has(id) && <span className="carte-nouvelle">Nouveau</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
